import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Input, Spinner } from '../components/ui';
import { recoverAccount, resendVerification } from '../lib/auth-onboarding';

function errorStatus(err: unknown): number | undefined {
  if (typeof err === 'object' && err !== null && 'status' in err) {
    const s = (err as { status: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return undefined;
}

type RecoveryAction = 'login_help' | 'reset_password' | 'resend_verification';
type RecoveryReason = 'already_exists' | 'rate_limited';

const ACTIONS: { value: RecoveryAction; label: string; description: string }[] = [
  {
    value: 'login_help',
    label: 'I want to log in',
    description: "We'll email you a link to log in.",
  },
  {
    value: 'reset_password',
    label: 'I forgot my password',
    description: "We'll email you a password reset link.",
  },
  {
    value: 'resend_verification',
    label: 'I never received the verification email',
    description: "We'll resend it if your account is still pending verification.",
  },
];

function isRecoveryReason(value: string | null): value is RecoveryReason {
  return value === 'already_exists' || value === 'rate_limited';
}

function reasonBanner(reason: RecoveryReason, email: string): { title: string; body: string } {
  if (reason === 'already_exists') {
    return {
      title: email ? `We already have an account for ${email}.` : 'We already have an account for that email.',
      body: 'Choose an option below to log in, reset your password, or resend the verification email.',
    };
  }
  return {
    title: 'Too many sign-up attempts.',
    body: 'For your security we paused new attempts from your network. Try again in a little while.',
  };
}

export default function AuthRecoverPage(): JSX.Element {
  const [params] = useSearchParams();
  const initialEmail = params.get('email') ?? '';
  const reasonParam = params.get('reason');

  const [email, setEmail] = useState(initialEmail);
  const [action, setAction] = useState<RecoveryAction>('login_help');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [reason, setReason] = useState<RecoveryReason | null>(
    isRecoveryReason(reasonParam) ? reasonParam : null,
  );

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      if (action === 'resend_verification') {
        await resendVerification(email.trim());
      } else {
        await recoverAccount(email.trim(), action);
      }
      setDone(true);
    } catch (err) {
      // Surface rate-limit errors only; anything else takes the
      // constant-time "accepted" path so we don't leak account existence.
      if (errorStatus(err) === 429) {
        setReason('rate_limited');
        setSubmitting(false);
        return;
      }
      setDone(true);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 sm:px-6 lg:px-8">
        <Card>
          <CardBody className="text-center">
            <h1 className="text-lg font-semibold text-text-primary">Check your inbox</h1>
            <p className="mt-2 text-sm text-text-secondary">
              If an account exists for that email, we've sent instructions to it.
            </p>
            <p className="mt-6 text-xs text-text-secondary">
              Didn't get anything? Wait a couple of minutes, check your spam folder, then try again.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16 sm:px-6 lg:px-8">
      {reason && (
        <div
          role="alert"
          data-testid="recovery-reason-banner"
          className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <p className="font-medium">{reasonBanner(reason, initialEmail).title}</p>
          <p className="mt-1 text-amber-800">{reasonBanner(reason, initialEmail).body}</p>
        </div>
      )}
      <Card>
        <CardHeader title="Get back into your account" />
        <CardBody>
          <form className="flex flex-col gap-5" onSubmit={handleSubmit} noValidate>
            <fieldset className="flex flex-col gap-3">
              <legend className="text-sm font-medium text-text-primary">What do you need?</legend>
              {ACTIONS.map((a) => (
                <label key={a.value} className="flex cursor-pointer items-start gap-2 rounded-md border border-border-tertiary px-3 py-2 hover:border-brand-carnaby-pink">
                  <input
                    type="radio"
                    name="recovery-action"
                    value={a.value}
                    checked={action === a.value}
                    onChange={() => setAction(a.value)}
                    className="mt-0.5 h-4 w-4 border-border-tertiary text-brand-carnaby-pink focus:ring-brand-carnaby-pink"
                  />
                  <span>
                    <span className="block text-sm font-medium text-text-primary">{a.label}</span>
                    <span className="block text-xs text-text-secondary">{a.description}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            <Input
              label="Your email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? <Spinner size="sm" /> : null}
              Send instructions
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
